import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Eye, EyeOff, Loader2 } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";

const PENDING_INVITE_KEY = "dental_os_pending_invite_code";

export default function Login() {
  const { session, signIn, signOut, resetPasswordForEmail, refreshClinics, clinics = [], clinicsFetched = false } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode]         = useState("login");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState(null);
  const [resetSent, setResetSent] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingInvitesLoading, setPendingInvitesLoading] = useState(false);
  const [pendingInvitesError, setPendingInvitesError] = useState(null);
  const [acceptingInviteCode, setAcceptingInviteCode] = useState(null);
  const [inviteActionError, setInviteActionError] = useState(null);

  useEffect(() => {
    if (!session?.user?.id || !clinicsFetched || clinics.length > 0) {
      setPendingInvites([]);
      setPendingInvitesLoading(false);
      setPendingInvitesError(null);
      return undefined;
    }

    let cancelled = false;
    setPendingInvitesLoading(true);
    setPendingInvitesError(null);
    setInviteActionError(null);

    supabase.rpc("list_my_pending_invitations").then(({ data, error: rpcError }) => {
      if (cancelled) return;
      if (rpcError) {
        setPendingInvites([]);
        setPendingInvitesError(rpcError.message ?? "Could not check pending invitations.");
        return;
      }
      setPendingInvites(Array.isArray(data) ? data : []);
    }).finally(() => {
      if (!cancelled) setPendingInvitesLoading(false);
    });

    return () => { cancelled = true; };
  }, [session?.user?.id, clinicsFetched, clinics.length]);

  if (session && !clinicsFetched) {
    return (
      <AuthLayout title="Checking your account" subtitle="Loading your clinic access.">
        <div className="text-sm text-slate-500">Loading...</div>
      </AuthLayout>
    );
  }

  if (session && clinics.length > 0) {
    const to = location.state?.from || "/dashboard";
    return <Navigate to={to} replace />;
  }

  if (session) {
    return (
      <AuthLayout
        title={pendingInvites.length ? "Accept your clinic invitation" : "Finish setup or switch account"}
        subtitle={pendingInvites.length ? "This account has a pending clinic invitation." : "This local account is signed in but has no clinic yet."}
        footer={pendingInvites.length ? null : (
          <>Need the wizard? <Link data-testid="login-finish-signup" to="/signup/clinic" className="text-teal-700 font-medium hover:underline">Finish clinic setup</Link></>
        )}
      >
        <div className="space-y-4" data-testid="login-clinicless-session">
          <p className="text-sm text-slate-600">
            Signed in as <span className="font-medium text-slate-900">{session.user?.email || "this local account"}</span>.
          </p>
          {pendingInvitesLoading ? (
            <div data-testid="login-pending-invites-loading" className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Checking pending invitations...
            </div>
          ) : null}
          {pendingInvites.length ? (
            <div data-testid="login-pending-invites" className="space-y-3">
              {pendingInvites.map((invite) => {
                const accepting = acceptingInviteCode === invite.code;
                const doctorInvite = invite.role === "doctor";
                return (
                  <div key={invite.code || invite.invitation_id} className="rounded-lg border border-teal-100 bg-teal-50 p-3">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-slate-900">{invite.clinic_name}</p>
                      <p className="text-xs text-slate-600">Invited as {roleLabel(invite.role)}.</p>
                    </div>
                    <button
                      data-testid="login-accept-invite-button"
                      type="button"
                      disabled={accepting}
                      onClick={() => doctorInvite ? continueToInvitation(invite) : handleAcceptInvite(invite)}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:bg-teal-400"
                    >
                      {accepting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                          Joining...
                        </>
                      ) : doctorInvite ? (
                        `Continue invitation to ${invite.clinic_name}`
                      ) : (
                        `Accept invitation to ${invite.clinic_name}`
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}
          {pendingInvitesError ? (
            <div data-testid="login-pending-invites-error" className="flex items-start gap-2 rounded-lg border border-amber-100 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <p>{pendingInvitesError}</p>
            </div>
          ) : null}
          {inviteActionError ? (
            <p data-testid="login-invite-action-error" className="text-sm text-rose-600">{inviteActionError}</p>
          ) : null}
          <button
            data-testid="login-signout-button"
            type="button"
            onClick={() => signOut()}
            className="w-full rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold py-2.5 transition"
          >
            Sign out and log in
          </button>
        </div>
      </AuthLayout>
    );
  }

  function continueToInvitation(invite) {
    if (invite?.code) window.sessionStorage.setItem(PENDING_INVITE_KEY, invite.code);
    navigate(`/join?code=${encodeURIComponent(invite.code || "")}`);
  }

  async function handleAcceptInvite(invite) {
    setInviteActionError(null);
    setAcceptingInviteCode(invite.code);
    const { data, error: inviteError } = await supabase.rpc("accept_invitation", {
      p_code: invite.code,
      p_full_name: invite.invitee_full_name || session?.user?.user_metadata?.full_name || null,
      p_display_name: null,
      p_prc_license: null,
    });
    if (inviteError || data?.ok === false) {
      setInviteActionError(inviteError?.message || data?.message || "That invitation could not be accepted.");
      setAcceptingInviteCode(null);
      return;
    }
    await supabase.auth.refreshSession();
    await refreshClinics?.();
    window.sessionStorage.removeItem(PENDING_INVITE_KEY);
    setAcceptingInviteCode(null);
    navigate("/dashboard", { replace: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: err } = await signIn(email.trim(), password);
    setSubmitting(false);
    if (err) {
      setError(err.message ?? "Sign-in failed.");
      return;
    }
    // onAuthStateChange in AuthContext will fire and the redirect above
    // will pick up the new session.
  }

  async function handleResetSubmit(e) {
    e.preventDefault();
    setError(null);
    setResetSent(false);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email is required.");
      return;
    }
    setSubmitting(true);
    const { error: err } = await resetPasswordForEmail(trimmedEmail);
    setSubmitting(false);
    if (err) {
      setError(err.message ?? "Could not send reset email.");
      return;
    }
    setResetSent(true);
  }

  function showResetForm() {
    setError(null);
    setResetSent(false);
    setMode("reset");
  }

  function showLoginForm() {
    setError(null);
    setResetSent(false);
    setMode("login");
  }

  if (mode === "reset") {
    return (
      <AuthLayout
        title="Reset your password"
        subtitle="Enter your account email and Dental OS will send a reset link."
        footer={
          <>Remembered it? <button type="button" data-testid="login-back-button" onClick={showLoginForm} className="text-teal-700 font-medium hover:underline">Back to sign in</button></>
        }
      >
        <form onSubmit={handleResetSubmit} className="space-y-5" data-testid="login-reset-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="login-reset-email">Email</label>
            <input
              id="login-reset-email"
              data-testid="login-reset-email-input"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
              placeholder="you@clinic.example"
            />
          </div>
          {error ? (
            <p data-testid="login-reset-error" className="text-sm text-rose-600">{error}</p>
          ) : null}
          {resetSent ? (
            <p data-testid="login-reset-success" className="rounded-lg border border-teal-100 bg-teal-50 px-3 py-2 text-sm text-teal-800">
              Check your email for the password reset link.
            </p>
          ) : null}
          <button
            data-testid="login-reset-submit-button"
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white text-sm font-semibold py-2.5 transition"
          >
            {submitting ? "Sending..." : "Send reset link"}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your clinic dashboard."
      footer={
        <>New here? <Link data-testid="login-go-signup" to="/signup" className="text-teal-700 font-medium hover:underline">Create your clinic</Link></>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="login-email">Email</label>
          <input
            id="login-email"
            data-testid="login-email-input"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
            placeholder="you@clinic.example"
          />
        </div>
        <div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="login-password">Password</label>
            <button
              data-testid="login-forgot-password-button"
              type="button"
              onClick={showResetForm}
              className="text-xs font-semibold text-teal-700 hover:underline"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative mt-1">
            <input
              id="login-password"
              data-testid="login-password-input"
              type={showPassword ? "text" : "password"}
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 pr-10 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
              placeholder="••••••••"
            />
            <button
              data-testid="login-password-visibility-button"
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-2 flex w-8 items-center justify-center rounded-md text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-100"
            >
              {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
            </button>
          </div>
        </div>
        {error ? (
          <p data-testid="login-error" className="text-sm text-rose-600">{error}</p>
        ) : null}
        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white text-sm font-semibold py-2.5 transition"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthLayout>
  );
}

function roleLabel(role) {
  return ({
    clinic_admin: "Clinic Admin",
    doctor: "Doctor",
    receptionist: "Receptionist",
    assistant: "Assistant",
  })[role] ?? role;
}
